/**
 * Merkflow Sidebar — nav row.
 *
 * Extracted from Sidebar.tsx. Renders a single NavLink row with the
 * orange gradient active pill, collapsed icon-only mode, and an animated
 * unread badge.
 */

import { NavLink } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { type NavItem } from './sidebarNav';

export function SidebarNavRow({
  item,
  active,
  collapsed,
  badge,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  badge?: number;
}) {
  const { t } = useTranslation();
  const { to, label: fallbackLabel, i18nKey, icon: Icon } = item;
  const label = i18nKey ? t(i18nKey) : fallbackLabel;
  // Stable testid derived from the last path segment so e2e tests can
  // address nav rows without relying on i18n labels (which differ
  // between en/nl). `/dashboard/posts` → nav-posts;
  // `/dashboard/settings/billing` → nav-billing. Empty last segment
  // (the dashboard root) → nav-overview.
  const lastSegment = to.replace(/\/$/, '').split('/').pop() || '';
  const testId = `nav-${lastSegment || 'overview'}`;
  return (
    <li className="relative">
      <NavLink
        to={to}
        end={item.exact}
        title={collapsed ? label : undefined}
        data-testid={testId}
        style={
          active
            ? {
                backgroundImage:
                  'linear-gradient(to right, var(--color-accent), color-mix(in oklab, var(--color-accent) 70%, #fff))',
                boxShadow:
                  '0 10px 15px -3px color-mix(in oklab, var(--color-accent) 25%, transparent)',
              }
            : undefined
        }
        className={`group relative flex w-full items-center rounded-xl px-3 py-2 text-sm font-medium transition-all duration-150 ${collapsed ? 'justify-center px-0' : ''} ${
          active
            ? 'font-semibold text-white'
            : 'text-text-secondary hover:bg-subtle hover:text-text-primary'
        } `}
      >
        <Icon className="h-[18px] w-[18px] flex-shrink-0" />
        {!collapsed && (
          <>
            <span className="ml-3 flex-1 truncate">{label}</span>
            <AnimatePresence>
              {badge !== undefined && badge > 0 && (
                <motion.span
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0, opacity: 0 }}
                  transition={{ type: 'spring', stiffness: 420, damping: 22 }}
                  className={`inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1.5 text-[10px] font-bold ${active ? 'bg-white/25 text-white ring-1 ring-white/40' : 'bg-accent text-white'} `}
                >
                  {badge > 99 ? '99+' : badge}
                </motion.span>
              )}
            </AnimatePresence>
          </>
        )}
      </NavLink>
    </li>
  );
}
